/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Controller, useWatch } from "react-hook-form";
import { useAuth, isGuestUser } from "../../../../context/AuthContext";
import { formatCurrency } from "../../../../utils/format";
import {
  computeTotals,
  getPrepaidPaymentData,
  getAmountConventional,
  recalcSalesPriceFromPayment,
} from "../utils/calculations";
import {
  isOriginationRow,
  calcOriginationDollarValue,
  getTemplateOriginationPercent,
  isDeliberateManualEdit,
  withOriginationFeeConverted,
} from "../../../../utils/originationFee";
import useFormSync from "../hooks/useFormSync";
import ControlledField from "./ControlledField";
import EscrowTitleFields from "./EscrowTitleFields";
import DisclaimerModal from "../../popups/DisclaimerModal";
import EmailModal from "../../popups/EmailModal";
import TextMessageModal from "../../popups/TextMessageModal";
import useManualFields from "../hooks/useManualFields";
import ManualField from "./ManualField";
import { useModal } from "../../../../context/ModalContext";
import { CommonHeader } from "../../../../components/ui/CommonHeader";
import { buildBuyerPayload } from "../../../../utils/payloadBuilder";
import GeneralInformationSection from "../../components/GeneralInformationSection";
import { defaultEst, computeCommission, parseCurrency } from "../utils/buyerNet.shared";
import AmortizationScheduleModal from "../../popups/AmortizationScheduleModal";
import { taxDebugLog } from "../../../../utils/taxDebugLogger";

export default function ManualSheetSection(props) {
  const {
    mainId,
    formData,
    setFormData,
    control,
    setValue,
    getValues,
    templateformData,
    FloatingInput,
    FloatingNumber,
    CustomSelect,
    handleCalAmount,
    checkValidSalesPrice,
    handleCurrencyBlur,
    isInitialLoad,
    showToaster,
    handleDownloadPrintPDF,
    handleClosingCostUpdate,
    callOnFirstLoanChange,
    callOnSecondLoanChange,
    callOnBuyerDownPaymentChange,
    isQuickMode,
    ttbData,
    isCorrectProperty,
    useAnnualPropertyTax,
    onAddressEnriched,
  } = props;

  const { user } = useAuth();
  const ucWords = (str = "") =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  const fullName = ucWords(
    `${user.first_name ?? ""} ${user.last_name ?? ""}`,
  ).trim();

  // ✅ REF to prevent calculation loops from address changes
  const isAddressCalculatingRef = useRef(false);

  // Use passed template and form data directly (no Redux)
  const buyerTemplateData = templateformData;
  const buyerformData = formData;

  // ✅ SAFE DEFAULTS FOR loanInfoMeta
  const safeLoanMeta = useMemo(
    () => ({
      buyersLoanType: "Conventional",
      lendername: "",
      allowOwnerCarry1Check: false,
      allowOwnerCarry2Check: false,
      conventionalPercent: "80.00",
      conventionalPercent2: "0.00",
      interestRate1: "0.00",
      interestRate2: "0.00",
      termsInYears1: "30",
      termsInYears2: "0",
      addDueYear1: "0.00",
      addDueYear2: "0.00",
      amount: "0.00",
      amount2: "0.00",
      adjusted: "0.00",
      buyerDownPayment: "0.00",
      noPMICheck: false,
      showAPRCheck: false,
      estimatedClosingDate: defaultEst,
      annualPropertyTax: "0.00",
      useForPrepaidCheck: false,
      ...(formData?.loanInfoMeta || {}),
    }),
    [formData?.loanInfoMeta],
  );

  // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
  useEffect(() => {
    // taxDebugLog("ManualSheetSection safeLoanMeta snapshot", {
    //   buyersLoanType: safeLoanMeta.buyersLoanType,
    //   annualPropertyTax: safeLoanMeta.annualPropertyTax,
    //   amount: safeLoanMeta.amount,
    //   buyerDownPayment: safeLoanMeta.buyerDownPayment,
    //   conventionalPercent: safeLoanMeta.conventionalPercent,
    //   requestedDownPayment: formData?.loanInfoMeta?.requestedDownPayment,
    //   rawFormDataLoanInfoMeta: formData?.loanInfoMeta,
    // });
  }, [
    safeLoanMeta.buyersLoanType,
    safeLoanMeta.annualPropertyTax,
    safeLoanMeta.amount,
    safeLoanMeta.buyerDownPayment,
    formData?.loanInfoMeta,
  ]);

  // ✅ SAFE DEFAULTS FOR prepaidsMeta
  const safePrepaidsMeta = useMemo(
    () => ({
      monthsTaxes: "0",
      rate: "0.00",
      taxesAmount: "0.00",
      monthsIns: "0",
      rateThou: "0.00",
      insAmount: "0.00",
    //  daysInterest: "0",
      daysInterestAmount: "0.00",
      twoMonthsPMI: "0.00",
      fhaMip: "0.00",
      fhaMipCheck: true,
      fhaMipAmount: "0.00",
      vaffFinance: "0.00",
      vaffFinanceCheck: true,
      vaffFinanceRate: "0.00",
      vaffFinanceAmount: "0.00",
      mipFinance: "0.00",
      mipFinanceCheck: true,
      mipFinanceAmount: "0.00",
      costs: "Other",
      costsAmount: "0.00",
      totalPrepaidItems: "0.00",
      ...(formData?.prepaidsMeta || {}),
    }),
    [formData?.prepaidsMeta],
  );

  // ✅ SAFE DEFAULTS FOR paymentMeta
  const safePaymentMeta = useMemo(
    () => ({
      principalAndInterest: "0.00",
      realEstateTaxes: "0.00",
      homeownerInsurance: "0.00",
      rateMonthlyPMI: "0.00",
      rateMonthlyPMIAmount: "0.00",
      pAndI2ndTDAmount: "0.00",
      monthlyExpense1: "Other",
      monthlyExpense2: "Other",
      monthlyExpense1Amount: "0.00",
      monthlyExpense2Amount: "0.00",
      totalMonthlyPayment: "0.00",
      estimatedTaxProrations: "0.00",
      ...(formData?.paymentMeta || {}),
    }),
    [formData?.paymentMeta],
  );

  // ✅ SAFE DEFAULTS FOR compensationMeta
  const safeCompensationMeta = useMemo(
    () => ({
      sellerBrokerCompensation: "0.00",
      sellerBrokerCompensationAmount: "0.00",
      buyerBrokerCompensation: "0.00",
      buyerBrokerCompensationAmount: "0.00",
      totalAgtAmount: "0.00",
      ...(formData?.compensationMeta || {}),
    }),
    [formData?.compensationMeta],
  );

  // ✅ SAFE DEFAULTS FOR totalClosingMeta
  const safeTotalClosingMeta = useMemo(
    () => ({
      totalInvestment: "0.00",
      totalCommission: "0.00",
      totalClosingCosts: "0.00",
      totalPrepaids: "0.00",
      totalMonthlyPayment: "0.00",
      ...(formData?.totalClosingMeta || {}),
    }),
    [formData?.totalClosingMeta],
  );

  const loanType = safeLoanMeta.buyersLoanType;

  // 2️⃣ Now safely initialize the sync helper with parent RHF methods
  useFormSync({
    methods: { control, setValue, getValues },
    setFormData,
    handleCalAmount,
    commissionEnabled: templateformData?.commission_buyer !== false,
  });

  // NOTE: previously there was a "watch RHF loanInfoMeta, sync into formData"
  // effect here. Every interactive loanInfoMeta field already calls
  // setFormData directly in its own onChange/onBlur, and useBuyerNetEngine's
  // "formData -> RHF" sync effects now keep RHF's copy correct from
  // formData's side. Syncing in both directions at once meant the two sides
  // never reliably converged (a field formatted differently between RHF's
  // raw value and formData's computed value would never satisfy the
  // equality check), which produced a confirmed infinite render loop
  // ("Maximum update depth exceeded") for VA/USDA. Removed.

  const watchedAddressMeta = useWatch({ control, name: "addressMeta" });
  const watchedAddressVerified = useWatch({
    control,
    name: "addressVerified",
  });

  useEffect(() => {
    if (!watchedAddressMeta || typeof watchedAddressMeta !== "object") return;

    setFormData((prev) => {
      const cur = prev?.addressMeta || {};
      const keys = Object.keys(watchedAddressMeta);
      const hasAddrChange = keys.some((k) => cur[k] !== watchedAddressMeta[k]);
      const verChange =
        watchedAddressVerified !== undefined &&
        prev.addressVerified !== watchedAddressVerified;

      // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
      // taxDebugLog("ManualSheetSection watchedAddressVerified sync", {
      //   watchedAddressVerified,
      //   prevAddressVerified: prev.addressVerified,
      //   verChange,
      //   hasAddrChange,
      // });

      if (!hasAddrChange && !verChange) return prev;
      const next = {
        ...prev,
        addressMeta: { ...cur, ...watchedAddressMeta },
        // Explicitly preserve preparedBy during address transitions
        preparedBy: prev?.preparedBy || getValues("preparedBy") || "",
      };
      if (verChange) next.addressVerified = watchedAddressVerified;
      return next;
    });

    // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
    // taxDebugLog("watchedAddressMeta effect guard check", {
    //   watchedAddressVerified,
    //   watchedAddressMetaZip: watchedAddressMeta?.zip,
    //   isAddressCalculatingRefCurrent: isAddressCalculatingRef.current,
    // });

    // ✅ FIXED: Recalculate loan / prepaid / title figures when verified address changes
    // Only trigger if not already calculating and address actually changed
    if (
      watchedAddressVerified &&
      watchedAddressMeta?.zip &&
      !isAddressCalculatingRef.current
    ) {
      const snapshot = getValues();
      if (
        snapshot?.loanInfoMeta?.buyersLoanType &&
        parseCurrency(snapshot?.salesPrice || 0) > 0
      ) {
        // Merge the latest address into the snapshot so calculations use new location
        snapshot.addressMeta = {
          ...(snapshot.addressMeta || {}),
          ...watchedAddressMeta,
        };
        snapshot.addressVerified = watchedAddressVerified;
        // Ensure preparedBy survives in the snapshot passed to the engine
        if (!snapshot.preparedBy && formData?.preparedBy) {
          snapshot.preparedBy = formData.preparedBy;
        }

        // ✅ Set guard before calling calculation
        isAddressCalculatingRef.current = true;

        // TEMP DEBUG — remove once the Domain A property-tax investigation is closed.
        // taxDebugLog("watchedAddressMeta handleCalAmount dispatch", {
        //   snapshotBuyersLoanType: snapshot?.loanInfoMeta?.buyersLoanType,
        //   formDataBuyersLoanType: formData?.loanInfoMeta?.buyersLoanType,
        //   snapshotAmount: snapshot?.loanInfoMeta?.amount,
        //   snapshotSalesPrice: snapshot?.salesPrice,
        // });

        try {
          handleCalAmount(snapshot);
        } finally {
          // ✅ Release guard after a short delay to let the calculation complete
          setTimeout(() => {
            isAddressCalculatingRef.current = false;
          }, 500);
        }
      }
    }
  }, [
    watchedAddressMeta,
    watchedAddressVerified,
    setFormData,
    getValues,
    handleCalAmount,
    formData?.preparedBy,
  ]);

  // Ported from PropertyDetailsSection.js's own copy of this effect: Quick
  // Mode goes straight to this component (PropertyDetailsSection, the
  // Guided module, never mounts), so without this here too,
  // loanInfoMeta.annualPropertyTax never gets populated from the property's
  // fetched tax amount (ttbData.taxAmount) when the address comes in via
  // Quick Mode / this Manual Sheet.
  useEffect(() => {
    if (!isCorrectProperty) return;
    const rawTaxAmount = ttbData?.taxAmount;
    if (
      rawTaxAmount === undefined ||
      rawTaxAmount === null ||
      rawTaxAmount === ""
    ) {
      return;
    }

    const desired = useAnnualPropertyTax ? rawTaxAmount : "0.00";

    setFormData((prev) => {
      if (
        prev?.loanInfoMeta?.annualPropertyTax === desired &&
        prev?.loanInfoMeta?.annualPropertyTaxAmount === rawTaxAmount
      ) {
        return prev;
      }
      return {
        ...prev,
        loanInfoMeta: {
          ...(prev?.loanInfoMeta || {}),
          annualPropertyTaxAmount: rawTaxAmount,
          annualPropertyTax: desired,
        },
      };
    });

    // Keep react-hook-form's own value in sync too — otherwise it stays
    // whatever the last reset() stamped it as, and this component's own
    // RHF->formData sync effects would overwrite the correct plain-state
    // value above with that stale one.
    if (typeof setValue === "function") {
      setValue("loanInfoMeta.annualPropertyTax", desired, {
        shouldDirty: true,
        shouldTouch: true,
      });
      setValue("loanInfoMeta.annualPropertyTaxAmount", rawTaxAmount, {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [
    ttbData?.taxAmount,
    useAnnualPropertyTax,
    isCorrectProperty,
    setFormData,
    setValue,
  ]);

  const manualClosingCostsKey = useMemo(() => {
    const m = formData?.closingCostsMeta;
    if (!Array.isArray(m) || m.length === 0) return "";
    return m
      .map((row) =>
        [row?.camelKey, row?.label, row?.type, row?.value, row?.total].join(
          "\t",
        ),
      )
      .join("|");
  }, [formData?.closingCostsMeta]);

  const manualBuyerClosingKey = useMemo(() => {
    const m = formData?.buyerClosingCostsMeta;
    if (!Array.isArray(m) || m.length === 0) return "";
    return m
      .map((row) => [row?.camelKey, row?.label, row?.value].join("\t"))
      .join("|");
  }, [formData?.buyerClosingCostsMeta]);

  useEffect(() => {
    if (!manualClosingCostsKey) return;
    const meta = formData?.closingCostsMeta;
    if (!Array.isArray(meta)) return;
    const opts = { shouldDirty: false, shouldTouch: false };
    meta.forEach((item, index) => {
      const base = `closingCostsMeta.${index}`;
      setValue(`${base}.label`, item?.label ?? "", opts);
      setValue(`${base}.type`, item?.type ?? "", opts);
      setValue(
        `${base}.value`,
        item?.value != null ? String(item.value) : "",
        opts,
      );
      setValue(
        `${base}.total`,
        item?.total != null ? String(item.total) : "",
        opts,
      );
    });
  }, [manualClosingCostsKey, formData?.closingCostsMeta, setValue]);

  useEffect(() => {
    if (!manualBuyerClosingKey) return;
    const meta = formData?.buyerClosingCostsMeta;
    if (!Array.isArray(meta)) return;
    const opts = { shouldDirty: false, shouldTouch: false };
    meta.forEach((item) => {
      if (!item?.camelKey) return;
      setValue(
        item.camelKey,
        item?.value != null ? String(item.value) : "0.00",
        opts,
      );
    });
  }, [manualBuyerClosingKey, formData?.buyerClosingCostsMeta, setValue]);

  // TEMP DEBUG — buyerSections pipeline trace, final step: how
  // buyerClosingCostsMeta rows resolve to the rendered HTML.
  // useEffect(() => {
  //   const meta = formData?.buyerClosingCostsMeta;
  //   taxDebugLog("buyerClosingCostsMeta render snapshot", {
  //     buyersLoanType: formData?.loanInfoMeta?.buyersLoanType,
  //     count: Array.isArray(meta) ? meta.length : 0,
  //     rows: Array.isArray(meta)
  //       ? meta.map((item) => ({
  //         camelKey: item?.camelKey ?? null,
  //         label: item?.label ?? "",
  //         apply: item?.apply ?? null,
  //         value: item?.value ?? null,
  //         defaultValueUsedByInput: item?.value ?? "0.00",
  //       }))
  //       : meta,
  //   });
  // }, [manualBuyerClosingKey, formData?.buyerClosingCostsMeta]);

  // ✅ FIXED: preparedBy effect - preserve existing value
  useEffect(() => {
    if (user?.first_name || user?.last_name) {
      setFormData((prev) => ({
        ...prev,
        // Preserve existing preparedBy if already present (e.g. loaded from saved calc)
        preparedBy: prev?.preparedBy || fullName,
      }));

      // Only set RHF value if it's not already set
      const currentPreparedBy = getValues("preparedBy");
      if (!currentPreparedBy) {
        setValue("preparedBy", fullName, {
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    }
  }, [user, setFormData, fullName, setValue, getValues]);

  // Escrow edit fields setting
  const [EscrowDisabled, setEscrowDisabled] = useState({
    escrowSettlement: true,
    ownersPolicy: true,
    lendersPolicy: true,
    premiumText: "",
  });

  const { openModal, closeModal } = useModal();

  // ── Throttle rapid Tab key presses ──
  useEffect(() => {
    let lastTabTime = 0;
    const TAB_THROTTLE_MS = 400;

    const handleKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const now = Date.now();
      if (now - lastTabTime < TAB_THROTTLE_MS) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      lastTabTime = now;
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, []);

    // useEffect(() => {
    //   console.log("formData", formData);
    // }, [formData]);

  // TEMP DEBUG — traces every write to buyerClosingCostsMeta's origination
  // row, tagged by whichever writer's `phase` value is current, so the full
  // ordered sequence of writes shows up in one pass regardless of which
  // handler (origination effect, Loan Type change, manual field edit,
  // getTitleEscrowData, useUserTemplate reload) did it. Remove once the
  // Origination Fee not-updating investigation is fully closed.
  // useEffect(() => {
  //   console.log("[trace] buyerClosingCostsMeta origination row changed", {
  //     phase: formData?.phase,
  //     row: formData?.buyerClosingCostsMeta?.find((it) => isOriginationRow(it)),
  //   });
  // }, [formData?.phase, formData?.buyerClosingCostsMeta]);

  useEffect(() => {
    if (isInitialLoad) return;
    const metaList = formData?.buyerClosingCostsMeta;
    if (!Array.isArray(metaList) || !metaList.length) return;

    const updated = metaList.map((obj) => {
      if (!isOriginationRow(obj)) return obj;

      if (obj.isManual) {
        return {
          ...obj,
          camelKey: "originationFee",
          key: "origination_fee",
          label: "Origination Fee",
          unit: "$",
        };
      }

      const salesPrice =
        parseFloat(String(formData.salesPrice || 0).replace(/,/g, "")) || 0;
      if (!(salesPrice > 0)) return obj;

      // The original percentage never needs preserving on this row — it
      // already lives untouched in the loaded template data, which this
      // effect never mutates. Reading it from there means a later Sales
      // Price change always has the real percentage to recompute from.
      // Previously only `value` was updated here and `total` (the field
      // actually sent to the backend/PDF for further use) stayed frozen at
      // the raw percent forever.
      const originalPercent = getTemplateOriginationPercent(
        buyerTemplateData?.conventional,
      );
      const formatted = calcOriginationDollarValue(originalPercent, salesPrice);

      return {
        ...obj,
        camelKey: "originationFee",
        key: "origination_fee",
        label: "Origination Fee",
        unit: "$",
        value: formatted,
        total: formatted,
        isManual: false,
      };
    });

    const isChanged = JSON.stringify(metaList) !== JSON.stringify(updated);

    // console.log("[originationFee] decision", {
    //   isChanged,
    //   metaListOriginationRow: metaList.find((it) => isOriginationRow(it)),
    //   updatedOriginationRow: updated.find((it) => isOriginationRow(it)),
    // });

    if (isChanged) {
      setFormData((prev) => {
        const next = {
          ...prev,
          phase: "buyerClosingCostsMeta",
          buyerClosingCostsMeta: updated,
        };
        // console.log("[originationFee] setFormData updater ran", {
        //   prevOriginationRow: prev?.buyerClosingCostsMeta?.find((it) =>
        //     isOriginationRow(it),
        //   ),
        //   nextOriginationRow: next.buyerClosingCostsMeta?.find((it) =>
        //     isOriginationRow(it),
        //   ),
        // });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.salesPrice, formData.buyerClosingCostsMeta, buyerTemplateData]);

  useEffect(() => {
    if (user?.first_name || user?.last_name) {
      setFormData((prev) => ({
        ...prev,
        // Preserve existing preparedBy if already present (e.g. loaded from saved calc)
        preparedBy: prev?.preparedBy || fullName,
      }));

      setValue("preparedBy", fullName, {
        shouldDirty: false,
        shouldTouch: false,
      });
    }
  }, [user, setFormData, fullName, setValue]);

  // ✅ Auto-calculate commission amounts on initial load
  useEffect(() => {
    if (isInitialLoad) return;

    if (templateformData?.commission_buyer === false) return;

    const sp = parseCurrency(formData.salesPrice);
    if (!sp || sp < 50000) return;

    const comp = formData?.compensationMeta;
    if (!comp) return;

    const sellerPercent = parseCurrency(comp.sellerBrokerCompensation);
    const buyerPercent = parseCurrency(comp.buyerBrokerCompensation);
    const sellerAmount = parseCurrency(comp.sellerBrokerCompensationAmount);
    const buyerAmount = parseCurrency(comp.buyerBrokerCompensationAmount);

    const hasPercents = sellerPercent > 0 || buyerPercent > 0;
    const noAmounts = sellerAmount === 0 && buyerAmount === 0;

    if (hasPercents && noAmounts) {
      // Functional updater, not setFormData(computeCommission(formData, true))
      // — this effect and the Origination Fee conversion effect above share
      // the same formData.salesPrice dependency, so both can run in the
      // same React effects pass. computeCommission shallow-spreads
      // whatever formData it's given, so calling it with THIS effect's own
      // (pre-conversion) closure and writing the result directly would
      // silently supersede the conversion effect's own queued update when
      // React applies both — a direct setFormData(value) always replaces
      // whatever's queued before it in the same batch, functional updater
      // or not, which is exactly how this kept undoing that conversion.
      setFormData((prev) => computeCommission(prev, true));
    }
  }, [
    formData?.salesPrice,
    formData?.compensationMeta?.sellerBrokerCompensation,
    formData?.compensationMeta?.buyerBrokerCompensation,
    formData?.compensationMeta?.sellerBrokerCompensationAmount,
    formData?.compensationMeta?.buyerBrokerCompensationAmount,
    templateformData?.commission_buyer,
    isInitialLoad,
    setFormData,
  ]);

  useEffect(() => {
    if (isInitialLoad) return;
    const totals = computeTotals(
      formData,
      templateformData?.commission_buyer !== false,
    );
    setFormData((prev) => {
      const existing = prev?.totalClosingMeta || {};

      const changed =
        totals.totalCommission !== existing.totalCommission ||
        totals.totalClosingCosts !== existing.totalClosingCosts ||
        totals.totalPrepaids !== existing.totalPrepaids ||
        totals.totalInvestment !== existing.totalInvestment ||
        totals.totalMonthlyPayment !== existing.totalMonthlyPayment;

      if (!changed) return prev;
      return {
        ...prev,
        phase: "totalClosingMeta",
        totalClosingMeta: {
          ...existing,
          totalCommission: totals.totalCommission,
          totalClosingCosts: totals.totalClosingCosts,
          totalPrepaids: totals.totalPrepaids,
          totalMonthlyPayment: totals.totalMonthlyPayment,
          totalInvestment: totals.totalInvestment,
        },
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.buyerClosingCostsMeta,
    formData.closingCostsMeta,
    formData.titleSettlementMeta,
    formData.compensationMeta,
    formData.prepaidsMeta,
    formData.paymentMeta,
    templateformData?.commission_buyer,
  ]);

  useEffect(() => {
    if (
      (!formData?.closingCostsMeta || formData.closingCostsMeta.length === 0) &&
      buyerformData?.closingCostsMeta &&
      Array.isArray(buyerformData.closingCostsMeta) &&
      buyerformData.closingCostsMeta.length > 0
    ) {
      setFormData((prev) => ({
        ...prev,
        closingCostsMeta: buyerformData.closingCostsMeta,
      }));
    }
  }, [
    buyerformData?.closingCostsMeta,
    formData?.closingCostsMeta,
    setFormData,
  ]);

  // ✅ Watch for changes in prepaidsMeta input/output fields and trigger recalculation
  useEffect(() => {
    if (!formData?.prepaidsMeta) return;

    const result = {
      amount: parseCurrency(formData?.loanInfoMeta?.amount),
      adjusted: parseCurrency(formData?.loanInfoMeta?.adjusted),
    };

    const prepaidData = getPrepaidPaymentData(formData, result);
    const isCash = formData?.loanInfoMeta?.buyersLoanType === "Cash";

    // console.log("Response 3 : " + JSON.stringify(prepaidData));
    setFormData((prev) => {
      const updated = {
        ...prev,
        prepaidsMeta: {
          ...prev.prepaidsMeta,
          taxesAmount: prepaidData.prepaidMonthTaxes,
          insAmount: prepaidData.monthInsurance,
          daysInterestAmount: prepaidData.daysInterestAmount,
        },
        paymentMeta: {
          ...prev.paymentMeta,
          principalAndInterest: prepaidData.principalAndInterest,
          homeownerInsurance: isCash ? "0.00" : prepaidData.homeOwnerInsuranceRes,
          realEstateTaxes: prepaidData.realEstateTaxes,
        },
      };

      const totals = computeTotals(
        updated,
        templateformData?.commission_buyer !== false,
      );
      updated.totalClosingMeta = {
        ...updated.totalClosingMeta,
        ...totals,
      };
      return updated;
    });
  }, [
    formData?.prepaidsMeta?.insAmount,
    formData?.prepaidsMeta?.daysInterestAmount,
    formData?.prepaidsMeta?.rate,
    formData?.prepaidsMeta?.monthsTaxes,
  ]);

  const closingCostsMeta =
    formData?.closingCostsMeta?.length > 0
      ? formData.closingCostsMeta
      : buyerformData?.closingCostsMeta?.length > 0
        ? buyerformData.closingCostsMeta
        : [];

  const closingCostsMetaRows = useManualFields(closingCostsMeta);
 console.log("ManualSheetSection closingCostsMetaRows:", closingCostsMetaRows);

  const cleanNumber = (v) => {
    if (v === undefined || v === null) return "";
    const s = typeof v === "string" ? v : String(v);
    return s.replace(/[^0-9.]/g, "");
  };

  // Estimated Tax Prorations can legitimately be a credit (negative) rather than a debit —
  // cleanNumber strips the sign, so this field needs its own negative-preserving variant
  // (paired with FloatingNumber's allowNegative).
  const cleanSignedNumber = (v) => {
    if (v === undefined || v === null) return "";
    const s = typeof v === "string" ? v : String(v);
    const negative = /^\s*-/.test(s);
    const digits = s.replace(/[^0-9.]/g, "");
    return negative && digits ? `-${digits}` : negative ? "-" : digits;
  };

  const handleEscrowTitleFields = (fieldName, formatted) => {
    setFormData((prev) => ({
      ...prev,
      phase: "titleSettlementMeta",
      titleSettlementMeta: {
        ...prev.titleSettlementMeta,
        [fieldName]: formatted,
      },
    }));
    setValue(fieldName, formatted);
  };

  const handleContinue = (setting) => {
    const pstString = new Date().toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
    });
    const [pstDate, pstTimeFull] = pstString.split(", ");
    const pstTime =
      pstTimeFull?.split(":").slice(0, 2).join(":") +
      (pstTimeFull?.includes("AM") ? " AM" : " PM");

    let premiumText = `*Premium changed ${fullName} ${pstDate} ${pstTime} PST`;
    setEscrowDisabled((prev) => ({
      ...prev,
      ...setting,
      premiumText: premiumText,
    }));

    if (setting.escrowSettlement === false) {
      setFormData((prev) => ({
        ...prev,
        phase: "escrowSettlementMeta",
        titleSettlementMeta: {
          ...prev.titleSettlementMeta,
          escrowSettlement: false,
          premiumText: premiumText,
        },
      }));
    }
    if (setting.ownersPolicy === false) {
      setFormData((prev) => ({
        ...prev,
        phase: "ownersPolicyMeta",
        titleSettlementMeta: {
          ...prev.titleSettlementMeta,
          ownersPolicy: false,
          premiumText: premiumText,
        },
      }));
    }
    if (setting.lendersPolicy === false) {
      setFormData((prev) => ({
        ...prev,
        phase: "lendersPolicyMeta",
        titleSettlementMeta: {
          ...prev.titleSettlementMeta,
          lendersPolicy: false,
          premiumText: premiumText,
        },
      }));
    }
    setFormData((prev) => ({
      ...prev,
      phase: "escrowPremiumChanged",
      escrowPremiumChanged: true,
    }));
    closeModal();
  };

  const handleActionButton = (action, buyerformData = []) => {
    const isValid = checkValidSalesPrice();
    if (!isValid) {
      return;
    }

    if (action === "download" || action === "print") {
      handleDownloadPrintPDF(action, buyerformData);
      return;
    }
    const user = JSON.parse(localStorage.getItem("user"));
    const formData = buildBuyerPayload({ buyerformData, user });

    const modals = {
      email: {
        title: "Email a Closing Cost Estimate",
        content: (
          <EmailModal
            onClose={closeModal}
            type="buyer"
            showToaster={showToaster}
            buyerFormData={formData}
            isQuickMode={isQuickMode}
          />
        ),
      },
      text: {
        title: "Text Message to Customer",
        content: (
          <TextMessageModal
            onClose={closeModal}
            type="buyer"
            showToaster={showToaster}
            buyerFormData={formData}
            isQuickMode={isQuickMode}
          />
        ),
      },
      AmortizationSch: {
        title: "Amortization Schedule",
        content: (
          <AmortizationScheduleModal
            onClose={closeModal}
            type="buyer"
            showToaster={showToaster}
            buyerFormData={formData}
            handleEmail={handleAmortizationEmail}
            handlePrint={handleAmortizationPrint}
          />
        ),
      },
      disclaimer: {
        title: "Disclaimer",
        content: <DisclaimerModal calc="buyer" onClose={closeModal} />,
      },
    };

    if (modals[action]) {
      if (action === "AmortizationSch") {
        openModal({ ...modals[action], size: "halffull" });
      } else {
        openModal({ ...modals[action], size: "mediumsizewidth" });
      }
    }
  };

  const handleAmortizationEmail = () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const formData = buildBuyerPayload({ buyerformData, user });

    openModal({
      title: "Email Amortization Schedule",
      size: "mediumsizewidth",
      content: (
        <EmailModal
          onClose={closeModal}
          type="buyer"
          calc="AmortizationSch"
          showToaster={showToaster}
          buyerFormData={formData}
        />
      ),
    });
  };

  const handleAmortizationPrint = () => {
    handleDownloadPrintPDF("print", buyerformData, "AmortizationSch");
  };

  return (
    <>
      <div className="model-body-calc-pg">
        <div className="calculator-page application-fe">
          <CommonHeader title={`Buyer's Closing Costs`}>
            <div className="ch-btnsave">
              <button
                type="button"
                value="New"
                className="ch-btnsave-new"
                onClick={() => props.onCloseManual()}
              >
                New
              </button>
              {!isGuestUser(user) && (
                <button
                  type="button"
                  value="Save"
                  className="ch-btnsave-save"
                  onClick={() => props.handleSaveCalc()}
                >
                  Save
                </button>
              )}
            </div>
          </CommonHeader>

          <div className="row-three-wrap">
            <div className="calc-column-a">
              <GeneralInformationSection
                control={control}
                getValues={getValues}
                setValue={setValue}
                user={user}
                openModal={openModal}
                closeModal={closeModal}
                setCompanyId={props?.setCompanyId}
                showToaster={showToaster}
                onAddressEnriched={onAddressEnriched}
                mode="buyer"
              />

              <h3 className="m-top-24 m-btm-16">
                Sale Price and Loan Info (Required)
              </h3>
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="salesPrice"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="Sales Price"
                    placeholder="0.00"
                    type="text"
                    className="input-filled"
                    defaultValue={formData.salesPrice || ""}
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) => {
                      setFormData((p) => ({
                        ...p,
                        phase: "salesPrice",
                        salesPrice: formatted,
                      }));
                    }}
                  />
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <span className="heading-c">Loan Information</span>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <label className="lbl-section">Loan Type</label>
                  <Controller
                    name="loanInfoMeta.buyersLoanType"
                    control={control}
                    defaultValue={safeLoanMeta.buyersLoanType}
                    render={({ field }) => (
                      <CustomSelect
                        {...field}
                        name="loanInfoMeta.buyersLoanType"
                        placeholder={field.value || "Select Loan Type"}
                        options={[
                          "FHA 203b",
                          "VA",
                          "USDA",
                          "Conventional",
                          "Cash",
                          "Owner Carry",
                        ]}
                        onChange={(value) => {
                          field.onChange(value);

                          setFormData((prev) => {
                            const prevLoan = prev?.loanInfoMeta ?? {};
                            const prevPrepaids = prev?.prepaidsMeta ?? {};
                            const wasCash = prevLoan.buyersLoanType === "Cash";
                            const isCash = value === "Cash";

                            let prepaidsMetaUpdate = {};
                            if (isCash && !wasCash) {
                              prepaidsMetaUpdate = {
                                monthsTaxesBeforeCash: prevPrepaids.monthsTaxes,
                                monthsTaxes: "0",
                              };
                            } else if (!isCash && wasCash) {
                              prepaidsMetaUpdate = {
                                monthsTaxes:
                                  prevPrepaids.monthsTaxesBeforeCash ??
                                  prevPrepaids.monthsTaxes,
                              };
                            }

                            let selectedMeta =
                              buyerTemplateData.conventional || [];

                            switch (value) {
                              case "FHA 203b":
                                selectedMeta = buyerTemplateData.fha || [];
                                break;
                              case "VA":
                                selectedMeta = buyerTemplateData.va || [];
                                break;
                              case "USDA":
                                selectedMeta = buyerTemplateData.usda || [];
                                break;
                              case "Cash":
                                selectedMeta = buyerTemplateData.cash || [];
                                break;
                              case "Owner Carry":
                                selectedMeta =
                                  buyerTemplateData.conventional || [];
                                break;
                              default:
                                selectedMeta =
                                  buyerTemplateData.conventional || [];
                            }

                            // TEMP DEBUG — remove once the loan-to-value investigation is closed.
                            // taxDebugLog("buyersLoanType switch", {
                            //   from: prevLoan.buyersLoanType,
                            //   to: value,
                            //   conventionalPercent: prevLoan.conventionalPercent,
                            //   requestedDownPayment: prevLoan.requestedDownPayment,
                            //   buyerDownPayment: prevLoan.buyerDownPayment,
                            // });

                            // TEMP DEBUG — buyerSections pipeline trace, step 4: which
                            // array got selected for the new loan type, and what
                            // credit_report looks like inside it right before it becomes
                            // formData.buyerClosingCostsMeta.
                            taxDebugLog("ManualSheetSection: buyerClosingCostsMeta selected for loan type", {
                              to: value,
                              creditReportInSelectedMeta: selectedMeta.find(
                                (it) => it?.key === "credit_report",
                              ),
                            });

                            return {
                              ...prev,
                              phase: "buyerClosingCostsMeta 12",
                              loanInfoMeta: {
                                ...prevLoan,
                                buyersLoanType: value,
                              },
                              prepaidsMeta: {
                                ...prevPrepaids,
                                ...prepaidsMetaUpdate,
                              },
                              // Same reasoning as the origination-fee effect
                              // above: a fresh template selection is always
                              // the raw, unconverted percent, and without
                              // converting it here, switching loan type
                              // would silently undo any Origination Fee
                              // calculation the user is currently looking
                              // at. selectedMeta is both the array being
                              // written AND the source for its own
                              // origination percent, since a non-Conventional
                              // loan type's section can have its own
                              // distinct Origination Factor value.
                              buyerClosingCostsMeta: withOriginationFeeConverted(
                                selectedMeta,
                                selectedMeta,
                                prev.salesPrice,
                              ),
                            };
                          });
                        }}
                      />
                    )}
                  />
                </div>

                <div className="calc-frm-fld form-group">
                  <Controller
                    name="loanInfoMeta.lendername"
                    control={control}
                    defaultValue={safeLoanMeta.lendername}
                    render={({ field }) => (
                      <FloatingInput
                        {...field}
                        placeholder="Name Of Lenders"
                        type="text"
                        onBlur={(e) =>
                          setFormData((p) => ({
                            ...p,
                            loanInfoMeta: {
                              ...p.loanInfoMeta,
                              lendername: e.target.value,
                            },
                          }))
                        }
                      />
                    )}
                  />
                  <label htmlFor="input-lendername">Name Of Lenders</label>
                </div>
              </div>

              <div className="calc-frm-field field-baseline">
                {/* First Left division */}
                <div className="calc-frm-field-division">
                  {loanType === "Owner Carry" && (
                    <div className="calc-frm-fld form-group">
                      <Controller
                        name="loanInfoMeta.allowOwnerCarry1Check"
                        control={control}
                        defaultValue={safeLoanMeta.allowOwnerCarry1Check}
                        render={({ field }) => (
                          <p>
                            <input
                              type="checkbox"
                              checked={!!field.value}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                field.onChange(checked);
                                setFormData((p) => ({
                                  ...p,
                                  loanInfoMeta: {
                                    ...(p?.loanInfoMeta || {}),
                                    allowOwnerCarry1Check: checked,
                                  },
                                }));
                              }}
                            />
                            <b>Owner Carry</b>
                          </p>
                        )}
                      />
                    </div>
                  )}

                  {/* 1st Loan */}
                  {(loanType === "Conventional" ||
                    loanType === "Owner Carry") && (
                      <>
                        {(loanType !== "Owner Carry" ||
                          safeLoanMeta.allowOwnerCarry1Check) && (
                            <>
                              <div className="calc-frm-fld form-group">
                                <span>
                                  <b>1st Loan</b>
                                </span>
                              </div>

                              <div className="calc-frm-fld form-group">
                                <FloatingNumber
                                  type="text"
                                  name="loanInfoMeta.conventionalPercent"
                                  value={safeLoanMeta.conventionalPercent}
                                  control={control}
                                  placeholder="0.00"
                                  className="inpt-pla"
                                  onChange={(e) => { }}
                                  onBlur={(e) => {
                                    // TEMP DEBUG — remove once the loan-to-value investigation is closed.
                                    // taxDebugLog("conventionalPercent onBlur fired", {
                                    //   eTargetValue: e?.target?.value,
                                    //   currentLoanType: formData?.loanInfoMeta?.buyersLoanType,
                                    //   priorConventionalPercent: formData?.loanInfoMeta?.conventionalPercent,
                                    // });
                                    const updated = {
                                      ...formData,
                                      loanInfoMeta: {
                                        ...formData.loanInfoMeta,
                                        conventionalPercent: e.target.value,
                                      },
                                    };
                                    setFormData(updated);
                                    callOnFirstLoanChange(updated);
                                  }}
                                />
                                <label>Loan to Value %</label>
                              </div>
                            </>
                          )}
                      </>
                    )}

                  {(loanType !== "Owner Carry" ||
                    safeLoanMeta.allowOwnerCarry1Check) && (
                      <>
                        <div className="calc-frm-fld form-group">
                          <FloatingNumber
                            type="text"
                            name="loanInfoMeta.interestRate1"
                            control={control}
                            placeholder="0.00"
                            className="inpt-pla"
                            value={safeLoanMeta.interestRate1}
                            decimals={4}
                            onChange={(e) => { }}
                            onBlur={(e) => {
                              const updated = {
                                ...formData,
                                loanInfoMeta: {
                                  ...formData.loanInfoMeta,
                                  interestRate1: e.target.value,
                                },
                              };
                              setFormData(updated);
                              callOnFirstLoanChange(updated);
                            }}
                          />
                          <label>Interest Rate %</label>
                        </div>

                        <div className="calc-frm-fld form-group">
                          <FloatingNumber
                            type="text"
                            name="loanInfoMeta.termsInYears1"
                            control={control}
                            placeholder="0"
                            decimals={0}
                            className="inpt-pla"
                            value={safeLoanMeta.termsInYears1}
                            onChange={(e) => { }}
                            onBlur={(e) => {
                              const updated = {
                                ...formData,
                                loanInfoMeta: {
                                  ...formData.loanInfoMeta,
                                  termsInYears1: e.target.value,
                                },
                              };
                              setFormData(updated);
                              callOnFirstLoanChange(updated);
                            }}
                          />
                          <label>Terms in Years</label>
                        </div>
                      </>
                    )}

                  {loanType === "Owner Carry" &&
                    safeLoanMeta.allowOwnerCarry1Check && (
                      <div className="calc-frm-fld form-group">
                        <FloatingNumber
                          type="text"
                          name="loanInfoMeta.addDueYear1"
                          control={control}
                          placeholder="0.00"
                          className="inpt-pla"
                          value={safeLoanMeta.addDueYear1}
                          onChange={(e) => { }}
                          onBlur={(e) => {
                            setFormData((p) => ({
                              ...p,
                              loanInfoMeta: {
                                ...p.loanInfoMeta,
                                addDueYear1: e.target.value,
                              },
                            }));
                          }}
                        />
                        <label>Add Due Years</label>
                      </div>
                    )}
                </div>

                {/* Second Right division */}
                <div className="calc-frm-field-division">
                  {loanType === "Owner Carry" && (
                    <div className="calc-frm-fld form-group">
                      <Controller
                        name="loanInfoMeta.allowOwnerCarry2Check"
                        control={control}
                        defaultValue={safeLoanMeta.allowOwnerCarry2Check}
                        render={({ field }) => (
                          <p>
                            <input
                              type="checkbox"
                              checked={!!field.value}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                field.onChange(checked);
                                setFormData((p) => ({
                                  ...p,
                                  loanInfoMeta: {
                                    ...(p?.loanInfoMeta || {}),
                                    allowOwnerCarry2Check: checked,
                                  },
                                }));
                              }}
                            />
                            <b>Owner Carry</b>
                          </p>
                        )}
                      />
                    </div>
                  )}

                  {(loanType === "Conventional" ||
                    loanType === "Owner Carry") && (
                      <>
                        {(loanType !== "Owner Carry" ||
                          safeLoanMeta.allowOwnerCarry1Check) && (
                            <>
                              <div className="calc-frm-fld form-group">
                                <span>
                                  <b>2nd Loan</b>
                                </span>
                              </div>

                              <div className="calc-frm-fld form-group">
                                <FloatingNumber
                                  type="text"
                                  name="loanInfoMeta.conventionalPercent2"
                                  value={safeLoanMeta.conventionalPercent2}
                                  control={control}
                                  placeholder="0.00"
                                  className="inpt-pla"
                                  onChange={(e) => { }}
                                  onBlur={(e) => {
                                    const updated = {
                                      ...formData,
                                      loanInfoMeta: {
                                        ...formData.loanInfoMeta,
                                        conventionalPercent2: e.target.value,
                                      },
                                    };
                                    setFormData(updated);
                                    callOnSecondLoanChange(updated);
                                  }}
                                />
                                <label>Loan to Value %</label>
                              </div>
                            </>
                          )}
                      </>
                    )}

                  {(loanType !== "Owner Carry" ||
                    safeLoanMeta.allowOwnerCarry2Check) && (
                      <>
                        <div className="calc-frm-fld form-group">
                          <FloatingNumber
                            type="text"
                            name="loanInfoMeta.interestRate2"
                            control={control}
                            placeholder="0.00"
                            className="inpt-pla"
                            value={safeLoanMeta.interestRate2}
                            onChange={(e) => { }}
                            onBlur={(e) => {
                              const updated = {
                                ...formData,
                                loanInfoMeta: {
                                  ...formData.loanInfoMeta,
                                  interestRate2: e.target.value,
                                },
                              };
                              setFormData(updated);
                              callOnSecondLoanChange(updated);
                            }}
                          />
                          <label>Interest Rate %</label>
                        </div>

                        <div className="calc-frm-fld form-group">
                          <FloatingNumber
                            type="text"
                            name="loanInfoMeta.termsInYears2"
                            control={control}
                            placeholder="0"
                            decimals={0}
                            className="inpt-pla"
                            value={safeLoanMeta.termsInYears2}
                            onChange={(e) => { }}
                            onBlur={(e) => {
                              const updated = {
                                ...formData,
                                loanInfoMeta: {
                                  ...formData.loanInfoMeta,
                                  termsInYears2: e.target.value,
                                },
                              };
                              setFormData(updated);
                              callOnSecondLoanChange(updated);
                            }}
                          />
                          <label>Terms in Years</label>
                        </div>
                      </>
                    )}

                  {loanType === "Owner Carry" &&
                    safeLoanMeta.allowOwnerCarry2Check && (
                      <div className="calc-frm-fld form-group">
                        <FloatingNumber
                          type="text"
                          name="loanInfoMeta.addDueYear2"
                          control={control}
                          placeholder="0.00"
                          className="inpt-pla"
                          value={safeLoanMeta.addDueYear2}
                          onChange={(e) => { }}
                          onBlur={(e) => {
                            setFormData((p) => ({
                              ...p,
                              loanInfoMeta: {
                                ...p.loanInfoMeta,
                                addDueYear2: e.target.value,
                              },
                            }));
                          }}
                        />
                        <label>Add Due Years</label>
                      </div>
                    )}
                </div>
              </div>

              {/* Amount */}
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.amount"
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    value={safeLoanMeta.amount}
                    onChange={(e) => { }}
                    onBlur={(e) => {
                      setFormData((p) => ({
                        ...p,
                        loanInfoMeta: {
                          ...p.loanInfoMeta,
                          amount: e.target.value,
                        },
                      }));
                    }}
                  />
                  <label>Amount</label>
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.amount2"
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    value={safeLoanMeta.amount2}
                    onChange={(e) => { }}
                    onBlur={(e) => {
                      setFormData((p) => ({
                        ...p,
                        loanInfoMeta: {
                          ...p.loanInfoMeta,
                          amount2: e.target.value,
                        },
                      }));
                    }}
                  />
                  <label>Amount</label>
                </div>
              </div>

              {/* Adjusted */}
              {(loanType === "FHA 203b" ||
                loanType === "USDA" ||
                loanType === "VA") && (
                  <>
                    <div className="calc-frm-field">
                      <div className="calc-frm-fld form-group sign-of-amount">
                        <FloatingNumber
                          type="text"
                          name="loanInfoMeta.adjusted"
                          control={control}
                          placeholder="0.00"
                          className="inpt-pla"
                          value={safeLoanMeta.adjusted}
                          onChange={(e) => { }}
                          onBlur={(e) => {
                            setFormData((p) => ({
                              ...p,
                              loanInfoMeta: {
                                ...p.loanInfoMeta,
                                adjusted: e.target.value,
                              },
                            }));
                          }}
                        />
                        <label>Adjusted</label>
                      </div>
                    </div>
                  </>
                )}

              {/* Down Payment */}
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="loanInfoMeta.buyerDownPayment"
                    control={control}
                    placeholder="0.00"
                    className="input-filled"
                    value={safeLoanMeta.buyerDownPayment}
                    onChange={(e) => { }}
                    onBlur={(e) => {
                      setFormData((p) => ({
                        ...p,
                        loanInfoMeta: {
                          ...p.loanInfoMeta,
                          buyerDownPayment: e.target.value,
                          requestedDownPayment: e.target.value,
                        },
                      }));
                      callOnBuyerDownPaymentChange();
                    }}
                  />
                  <label>Down Payment</label>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  {(loanType === "Conventional" ||
                    loanType === "Owner Carry") && (
                      <div className="floating-input-wrapper">
                        <Controller
                          name="loanInfoMeta.noPMICheck"
                          control={control}
                          defaultValue={safeLoanMeta.noPMICheck}
                          render={({ field }) => (
                            <p>
                              <input
                                type="checkbox"
                                checked={!!field.value}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  field.onChange(checked);
                                  setFormData((p) => ({
                                    ...p,
                                    loanInfoMeta: {
                                      ...(p?.loanInfoMeta || {}),
                                      noPMICheck: checked,
                                    },
                                  }));
                                }}
                              />{" "}
                              No PMI
                            </p>
                          )}
                        />
                      </div>
                    )}
                </div>
                <div className="calc-frm-fld form-group">
                  <div className="floating-input-wrapper">
                    <Controller
                      name="loanInfoMeta.showAPRCheck"
                      control={control}
                      defaultValue={safeLoanMeta.showAPRCheck}
                      render={({ field }) => (
                        <p>
                          <input
                            type="checkbox"
                            checked={!!field.value}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              field.onChange(checked);
                              setFormData((p) => ({
                                ...p,
                                loanInfoMeta: {
                                  ...(p?.loanInfoMeta || {}),
                                  showAPRCheck: checked,
                                },
                              }));
                            }}
                          />{" "}
                          Show APR
                        </p>
                      )}
                    />
                  </div>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <Controller
                    name="loanInfoMeta.estimatedClosingDate"
                    control={control}
                    defaultValue={safeLoanMeta.estimatedClosingDate}
                    render={({ field }) => (
                      <FloatingInput
                        type="date"
                        name="loanInfoMeta.estimatedClosingDate"
                        label=""
                        placeholder="MM/DD/YYYY"
                        className="inpt-pla"
                        value={safeLoanMeta.estimatedClosingDate}
                        onChange={(e) =>
                          setFormData((p) => ({
                            ...p,
                            loanInfoMeta: {
                              ...(p?.loanInfoMeta ?? {}),
                              estimatedClosingDate: e.target.value,
                            },
                          }))
                        }
                      />
                    )}
                  />
                  <label htmlFor="input-amount1">Estimated Closing Date</label>
                </div>
              </div>

              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="loanInfoMeta.annualPropertyTax"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="Annual Property Tax $"
                    placeholder=""
                    type="text"
                    defaultValue={safeLoanMeta.annualPropertyTax}
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) => {
                      setFormData((p) => ({
                        ...p,
                        loanInfoMeta: {
                          ...(p.loanInfoMeta ?? {}),
                          annualPropertyTax: formatted,
                        },
                      }));
                    }}
                  />
                </div>

                <div className="calc-frm-fld form-group">
                  <Controller
                    name="loanInfoMeta.useForPrepaidCheck"
                    control={control}
                    defaultValue={safeLoanMeta.useForPrepaidCheck}
                    render={({ field }) => (
                      <p>
                        <input
                          type="checkbox"
                          checked={!!field.value}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            field.onChange(checked);
                            setFormData((p) => ({
                              ...p,
                              loanInfoMeta: {
                                ...(p?.loanInfoMeta || {}),
                                useForPrepaidCheck: checked,
                              },
                            }));
                          }}
                        />{" "}
                        Use for Prepaids ?
                      </p>
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="calc-column-b">
              <h3 className="m-btm-16">Closing Costs</h3>

              <EscrowTitleFields
                mainId={mainId}
                control={control}
                FloatingInput={FloatingInput}
                FloatingNumber={FloatingNumber}
                CustomSelect={CustomSelect}
                cleanNumber={cleanNumber}
                setValue={setValue}
                setFormData={setFormData}
                handleCurrencyBlur={handleCurrencyBlur}
                onFormatted={(fieldName, formatted) =>
                  handleEscrowTitleFields(fieldName, formatted)
                }
                EscrowDisabled={EscrowDisabled}
                onContinue={handleContinue}
                openModal={openModal}
                closeModal={closeModal}
                formData={formData}
              />

              <div
                key={loanType}
                className="calc-frm-field"
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gridRowGap: "0",
                }}
              >
                {console.log("ManualSheetSection buyerClosingCostsMeta:", formData?.buyerClosingCostsMeta)}
                {(formData?.buyerClosingCostsMeta ?? []).map((item, index) => (
                  <div
                    // item?.camelKey, not index: react-hook-form's Controller
                    // (inside ControlledField) needs a full remount — not
                    // just a prop update — when its `name` changes, which
                    // happens here the moment the Origination row converts
                    // ("originationFactor" -> "originationFee"). Without the
                    // remount, RHF can end up showing a stale value under
                    // the new name instead of the freshly calculated one.
                    key={item?.camelKey ?? index}
                    className="calc-frm-fld form-group sign-of-amount"
                  >
                    <ControlledField
                      name={item?.camelKey ?? ""}
                      label={item?.label ?? ""}
                      control={control}
                      InputComponent={FloatingNumber}
                      placeholder="0.00"
                      type="text"
                      defaultValue={item?.value ?? "0.00"}
                      cleanNumber={cleanNumber}
                      handleCurrencyBlur={handleCurrencyBlur}
                      onFormatted={(name, formatted) => {
                        const getFormData = structuredClone(formData);

                        const updatedMeta =
                          getFormData?.buyerClosingCostsMeta?.map((item) => {
                            if (item?.camelKey === name) {
                              return {
                                ...item,
                                value: formatted,
                                isManual: isDeliberateManualEdit(item),
                              };
                            }

                            return item;
                          });

                        setFormData((prev) => ({
                          ...prev,
                          buyerClosingCostsMeta: updatedMeta,
                        }));
                      }}
                    />
                  </div>
                ))}
              </div>

              {formData?.escrowPremiumChanged && (
                <p className="premium-txt">{EscrowDisabled.premiumText}</p>
              )}

              {templateformData?.commission_buyer !== false && (
                <>
                  <h3 className="m-btm-16">Commission/Brokerage</h3>

                  {/* ✅ Seller's Broker */}
                  <div className="calc-frm-field">
                    <div className="calc-frm-fld form-group sign-of-percentage">
                      <label className="lbl-section">
                        Seller's Broker Compensation
                      </label>
                      <ControlledField
                        name="compensationMeta.sellerBrokerCompensation"
                        control={control}
                        InputComponent={FloatingNumber}
                        placeholder="0.00"
                        defaultValue={safeCompensationMeta.sellerBrokerCompensation}
                        onFormatted={(name, formatted) => {
                          const getFormData = {
                            ...formData,
                            compensationMeta: {
                              ...(formData?.compensationMeta || {}),
                              sellerBrokerCompensation: formatted,
                            },
                          };

                          const computed = computeCommission(getFormData, true);
                          setFormData(computed);
                        }}
                      />
                    </div>

                    <div className="calc-frm-fld form-group">
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group sign-of-amount no-m-space">
                          <ControlledField
                            name="compensationMeta.sellerBrokerCompensationAmount"
                            control={control}
                            InputComponent={FloatingNumber}
                            placeholder="0.00"
                            defaultValue={
                              safeCompensationMeta.sellerBrokerCompensationAmount
                            }
                            onFormatted={(name, formatted) => {
                              const getFormData = {
                                ...formData,
                                compensationMeta: {
                                  ...(formData?.compensationMeta || {}),
                                  sellerBrokerCompensationAmount: formatted,
                                },
                              };

                              const computed = computeCommission(
                                getFormData,
                                false,
                              );
                              setFormData(computed);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ✅ Buyer's Broker */}
                  <div className="calc-frm-field">
                    <div className="calc-frm-fld form-group sign-of-percentage">
                      <label className="lbl-section">
                        Buyer's Broker Compensation
                      </label>
                      <ControlledField
                        name="compensationMeta.buyerBrokerCompensation"
                        control={control}
                        InputComponent={FloatingNumber}
                        placeholder="0.00"
                        defaultValue={safeCompensationMeta.buyerBrokerCompensation}
                        onFormatted={(name, formatted) => {
                          const getFormData = {
                            ...formData,
                            compensationMeta: {
                              ...(formData?.compensationMeta || {}),
                              buyerBrokerCompensation: formatted,
                            },
                          };

                          const computed = computeCommission(getFormData, true);
                          setFormData(computed);
                        }}
                      />
                    </div>

                    <div className="calc-frm-fld form-group">
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group sign-of-amount no-m-space">
                          <ControlledField
                            name="compensationMeta.buyerBrokerCompensationAmount"
                            control={control}
                            InputComponent={FloatingNumber}
                            placeholder="0.00"
                            defaultValue={
                              safeCompensationMeta.buyerBrokerCompensationAmount
                            }
                            onFormatted={(name, formatted) => {
                              const getFormData = {
                                ...formData,
                                compensationMeta: {
                                  ...(formData?.compensationMeta || {}),
                                  buyerBrokerCompensationAmount: formatted,
                                },
                              };

                              const computed = computeCommission(
                                getFormData,
                                false,
                              );
                              setFormData(computed);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* ✅ Total Agt */}
                  <div className="calc-frm-field">
                    <div className="calc-frm-fld form-group">
                      <span className="lbl-section">Total Agt</span>
                    </div>
                    <div className="calc-frm-fld form-group">
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group sign-of-amount no-m-space">
                          <FloatingNumber
                            name="compensationMeta.totalAgtAmount"
                            type="text"
                            value={safeTotalClosingMeta.totalCommission}
                            control={control}
                            placeholder="0.00"
                            disabled
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {/* ✅ Dynamic Closing Cost Rows */}
              <div className="table-calc-frm">
                <div className="table-calc-frm-inner">
                  <div className="calc-frm-field m-top-24">
                    <div className="calc-frm-fld form-group clmn-a-width">
                      <span className="heading-c">Cost</span>
                    </div>
                    <div className="calc-frm-fld form-group clmn-b-width">
                      <span className="heading-c">Type</span>
                    </div>
                    <div className="calc-frm-fld form-group clmn-c-width">
                      <span className="heading-c">Rate</span>
                    </div>
                    <div className="calc-frm-fld form-group fld-txt-right clmn-d-width">
                      <span className="heading-c">Total</span>
                    </div>
                  </div>

                  {closingCostsMetaRows.map((r) => (
                    <ManualField
                      key={r.left.camelKey}
                      row={r}
                      control={control}
                      FloatingInput={FloatingInput}
                      FloatingNumber={FloatingNumber}
                      CustomSelect={CustomSelect}
                      onFieldUpdate={handleClosingCostUpdate}
                    />
                  ))}
                </div>
              </div>

              {/* ✅ Total */}
              <div className="calc-frm-field">
                <div className="total-amunt-fld">
                  <div className="amount-txt heading-c">
                    Total Closing Costs
                  </div>
                  <div className="amount-number heading-c">
                    $ {formatCurrency(safeTotalClosingMeta.totalClosingCosts)}
                  </div>
                </div>
              </div>
            </div>

            <div className="calc-column-c">
              <h3 className="m-btm-16">Prepaids</h3>

              {/* ✅ Months Taxes */}
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <label className="lbl-section">Months Taxes</label>
                  <ControlledField
                    name="prepaidsMeta.monthsTaxes"
                    defaultValue={safePrepaidsMeta.monthsTaxes}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0"
                    type="text"
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    inputProps={{ decimals: 0 }}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          monthsTaxes: formatted,
                        },
                      }))
                    }
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-percentage">
                  <label className="lbl-section">Rate</label>
                  <ControlledField
                    name="prepaidsMeta.rate"
                    defaultValue={safePrepaidsMeta.rate}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    inputProps={{ decimals: 4 }}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          rate: formatted,
                        },
                      }))
                    }
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="prepaidsMeta.taxesAmount"
                    defaultValue={safePrepaidsMeta.taxesAmount}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          taxesAmount: formatted,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              {/* ✅ Months Insurance */}
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <label className="lbl-section">Months Ins.</label>
                  <ControlledField
                    name="prepaidsMeta.monthsIns"
                    defaultValue={safePrepaidsMeta.monthsIns}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0"
                    type="text"
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    inputProps={{ decimals: 0 }}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          monthsIns: formatted,
                        },
                      }))
                    }
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-percentage">
                  <label className="lbl-section">Rate/Thou</label>
                  <ControlledField
                    name="prepaidsMeta.rateThou"
                    defaultValue={safePrepaidsMeta.rateThou}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    inputProps={{ decimals: 4 }}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          rateThou: formatted,
                        },
                      }))
                    }
                  />
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="prepaidsMeta.insAmount"
                    defaultValue={safePrepaidsMeta.insAmount}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          insAmount: formatted,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              {/* ✅ Days Interest */}
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <label className="lbl-section">Days Interest</label>
                  <ControlledField
                    name="prepaidsMeta.daysInterest"
                    defaultValue={safePrepaidsMeta.daysInterest}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0"
                    type="text"
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    inputProps={{ decimals: 0 }}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          daysInterest: formatted,
                        },
                      }))
                    }
                  />
                </div>

                <div className="calc-frm-fld form-group"></div>
                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="prepaidsMeta.daysInterestAmount"
                    defaultValue={safePrepaidsMeta.daysInterestAmount}
                    control={control}
                    InputComponent={FloatingNumber}
                    placeholder="0.00"
                    type="text"
                    cleanNumber={cleanNumber}
                    handleCurrencyBlur={handleCurrencyBlur}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          daysInterestAmount: formatted,
                        },
                      }))
                    }
                  />
                </div>
              </div>

              {/* ✅ Conditional Prepaids based on loan type */}
              {(() => {
                switch (loanType) {
                  case "Conventional":
                  case "Owner Carry":
                    return (
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group"></div>
                        <div className="calc-frm-fld form-group"></div>
                        <div className="calc-frm-fld form-group sign-of-amount">
                          <ControlledField
                            name="prepaidsMeta.twoMonthsPMI"
                            control={control}
                            InputComponent={FloatingNumber}
                            label="Amount"
                            placeholder="0.00"
                            className="inpt-pla"
                            type="text"
                            defaultValue={safePrepaidsMeta.twoMonthsPMI}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) =>
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  twoMonthsPMI: formatted,
                                },
                              }))
                            }
                          />
                          <label htmlFor="input-amount1">2 Months PMI </label>
                        </div>
                      </div>
                    );

                  case "FHA 203b":
                    return (
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group">
                          <ControlledField
                            name="prepaidsMeta.fhaMip"
                            control={control}
                            InputComponent={FloatingNumber}
                            label=""
                            placeholder=""
                            type="text"
                            defaultValue={safePrepaidsMeta.fhaMip}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) =>
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  fhaMip: formatted,
                                },
                              }))
                            }
                          />
                          <label htmlFor="input-conventionalPercent">
                            FHA MIP Finance
                          </label>
                        </div>

                        <div className="calc-frm-fld form-group">
                          <Controller
                            name="prepaidsMeta.fhaMipCheck"
                            control={control}
                            defaultValue={safePrepaidsMeta.fhaMipCheck}
                            render={({ field }) => (
                              <p className="txt-center">
                                <input
                                  type="checkbox"
                                  checked={!!field.value}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    field.onChange(checked);
                                    setFormData((p) => ({
                                      ...p,
                                      prepaidsMeta: {
                                        ...(p?.prepaidsMeta || {}),
                                        fhaMipCheck: checked,
                                      },
                                    }));
                                  }}
                                />{" "}
                              </p>
                            )}
                          />
                        </div>

                        <div className="calc-frm-fld form-group sign-of-amount">
                          <ControlledField
                            name="prepaidsMeta.fhaMipAmount"
                            control={control}
                            InputComponent={FloatingNumber}
                            label="Amount"
                            placeholder="0.00"
                            className="inpt-pla"
                            type="text"
                            defaultValue={safePrepaidsMeta.fhaMipAmount}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) =>
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  fhaMipAmount: formatted,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    );

                  case "VA":
                    return (
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group">
                          <ControlledField
                            name="prepaidsMeta.vaffFinance"
                            control={control}
                            InputComponent={FloatingInput}
                            label=""
                            placeholder=""
                            type="text"
                            defaultValue={safePrepaidsMeta.vaffFinance}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) =>
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  vaffFinance: formatted,
                                },
                              }))
                            }
                          />
                          <label htmlFor="input-conventionalPercent">
                            VA FF Finance
                          </label>
                        </div>

                        <div className="calc-frm-fld form-group small-input-check">
                          <Controller
                            name="prepaidsMeta.vaffFinanceCheck"
                            control={control}
                            defaultValue={safePrepaidsMeta.vaffFinanceCheck}
                            render={({ field }) => (
                              <p>
                                <input
                                  type="checkbox"
                                  checked={!!field.value}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    field.onChange(checked);
                                    setFormData((p) => ({
                                      ...p,
                                      prepaidsMeta: {
                                        ...(p?.prepaidsMeta || {}),
                                        vaffFinanceCheck: checked,
                                      },
                                    }));
                                  }}
                                />{" "}
                              </p>
                            )}
                          />
                        </div>
                        <div className="calc-frm-fld form-group small-input-two">
                          <ControlledField
                            name="prepaidsMeta.vaffFinanceRate"
                            control={control}
                            InputComponent={FloatingInput}
                            label=""
                            placeholder=""
                            type="text"
                            defaultValue={safePrepaidsMeta.vaffFinanceRate}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) =>
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  vaffFinanceRate: formatted,
                                },
                              }))
                            }
                          />
                          <label htmlFor="input-conventionalPercent"></label>
                        </div>
                        <div className="calc-frm-fld form-group sign-of-amount">
                          <ControlledField
                            name="prepaidsMeta.vaffFinanceAmount"
                            control={control}
                            InputComponent={FloatingNumber}
                            label=""
                            placeholder="0.00"
                            className="inpt-pla"
                            type="text"
                            defaultValue={safePrepaidsMeta.vaffFinanceAmount}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) =>
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  vaffFinanceAmount: formatted,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    );

                  case "USDA":
                    return (
                      <div className="calc-frm-field">
                        <div className="calc-frm-fld form-group">
                          <ControlledField
                            name="prepaidsMeta.mipFinance"
                            control={control}
                            InputComponent={FloatingInput}
                            label=""
                            placeholder=""
                            type="text"
                            defaultValue={safePrepaidsMeta.mipFinance}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) =>
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  mipFinance: formatted,
                                },
                              }))
                            }
                          />
                          <label htmlFor="input-conventionalPercent">
                            MIP Finance
                          </label>
                        </div>

                        <div className="calc-frm-fld form-group">
                          <Controller
                            name="prepaidsMeta.mipFinanceCheck"
                            control={control}
                            defaultValue={safePrepaidsMeta.mipFinanceCheck}
                            render={({ field }) => (
                              <p className="txt-center">
                                <input
                                  type="checkbox"
                                  checked={!!field.value}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    field.onChange(checked);
                                    setFormData((p) => ({
                                      ...p,
                                      prepaidsMeta: {
                                        ...(p?.prepaidsMeta || {}),
                                        mipFinanceCheck: checked,
                                      },
                                    }));
                                  }}
                                />{" "}
                              </p>
                            )}
                          />
                        </div>

                        <div className="calc-frm-fld form-group sign-of-amount">
                          <ControlledField
                            name="prepaidsMeta.mipFinanceAmount"
                            control={control}
                            InputComponent={FloatingNumber}
                            label=""
                            placeholder="0.00"
                            className="inpt-pla"
                            type="text"
                            defaultValue={safePrepaidsMeta.mipFinanceAmount}
                            cleanNumber={cleanNumber}
                            onFormatted={(name, formatted) =>
                              setFormData((p) => ({
                                ...p,
                                prepaidsMeta: {
                                  ...(p?.prepaidsMeta ?? {}),
                                  mipFinanceAmount: formatted,
                                },
                              }))
                            }
                          />
                        </div>
                      </div>
                    );

                  default:
                    return null;
                }
              })()}

              {/* ✅ Costs */}
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group">
                  <ControlledField
                    name="prepaidsMeta.costs"
                    control={control}
                    InputComponent={FloatingInput}
                    label=""
                    placeholder=""
                    type="text"
                    defaultValue={safePrepaidsMeta.costs}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          costs: formatted,
                        },
                      }))
                    }
                  />
                  <label htmlFor="input-conventionalPercent">Costs</label>
                </div>

                <div className="calc-frm-fld form-group sign-of-amount">
                  <ControlledField
                    name="prepaidsMeta.costsAmount"
                    control={control}
                    InputComponent={FloatingNumber}
                    label="Amount"
                    placeholder="0.00"
                    className="inpt-pla"
                    type="text"
                    defaultValue={safePrepaidsMeta.costsAmount}
                    cleanNumber={cleanNumber}
                    onFormatted={(name, formatted) =>
                      setFormData((p) => ({
                        ...p,
                        prepaidsMeta: {
                          ...(p?.prepaidsMeta ?? {}),
                          costsAmount: formatted,
                        },
                      }))
                    }
                  />
                  <label htmlFor="input-amount1">Amount</label>
                </div>
              </div>

              {/* ✅ Total Prepaid Items */}
              <div className="calc-frm-field">
                <div className="calc-frm-fld form-group sign-of-amount">
                  <FloatingNumber
                    type="text"
                    name="prepaidsMeta.totalPrepaidItems"
                    label="Total Prepaid Items"
                    value={safeTotalClosingMeta.totalPrepaids}
                    control={control}
                    placeholder="0.00"
                    className="inpt-pla"
                    disabled
                  />
                </div>
              </div>

              {/* ✅ Payment Section */}
              <div className="box-wrap layout-box">
                <div className="box-layout-head">
                  <h3>Payment</h3>
                </div>
                <div className="wrap-middle-container">
                  {[
                    {
                      label: "Principal and Interest",
                      key: "principalAndInterest",
                    },
                    { label: "Real Estate Taxes", key: "realEstateTaxes" },
                    { label: "Homeowner Insurance", key: "homeownerInsurance" },
                  ].map((item) => (
                    <div className="totl-estimate pay-btm-space" key={item.key}>
                      <div className="totl-estimate-txt heading-c">
                        {item.label} $
                      </div>
                      <div className="totl-estimate-amnt heading-c">
                        {formatCurrency(safePaymentMeta[item.key]) ?? "0.00"}
                      </div>
                    </div>
                  ))}

                  {/* ✅ Monthly PMI */}
                  <div className="totl-estimate pay-btm-space">
                    <div className="totl-estimate-txt heading-c txt-w-input">
                      <div className="calc-frm-fld form-group">
                        <ControlledField
                          name="paymentMeta.rateMonthlyPMI"
                          control={control}
                          InputComponent={FloatingNumber}
                          defaultValue={safePaymentMeta.rateMonthlyPMI}
                          placeholder="0.00"
                          type="text"
                          inputProps={{ decimals: 5 }}
                          cleanNumber={cleanNumber}
                          handleCurrencyBlur={handleCurrencyBlur}
                          onFormatted={(name, formatted) =>
                            setFormData((p) => ({
                              ...p,
                              paymentMeta: {
                                ...(p?.paymentMeta ?? {}),
                                rateMonthlyPMI: formatted,
                                rateMonthlyPMIManual: true,
                              },
                            }))
                          }
                        />{" "}
                      </div>
                      Rate Monthly
                      {loanType === "Conventional" && <> PMI</>}
                      {loanType === "USDA" && <> MI</>}
                      {loanType === "FHA 203b" && <> MMI</>} $
                    </div>
                    <div className="totl-estimate-amnt heading-c">
                      {formatCurrency(safePaymentMeta.rateMonthlyPMIAmount) ?? "0.00"}
                    </div>
                  </div>

                  {/* ✅ P&I 2nd TD */}
                  <div className="totl-estimate">
                    <div className="totl-estimate-txt heading-c">
                      P & I 2nd TD $
                    </div>
                    <div className="totl-estimate-amnt heading-c">
                      {safePaymentMeta.pAndI2ndTDAmount ?? "0.00"}
                    </div>
                  </div>

                  {/* ✅ Monthly Expenses */}
                  {[1, 2].map((item, index) => (
                    <div
                      key={item}
                      className={`calc-frm-field ${index === 0 ? "mg-top-16 pd-top-16" : ""
                        }`}
                    >
                      <div className="calc-frm-fld form-group">
                        <ControlledField
                          name={`paymentMeta.monthlyExpense${item}`}
                          control={control}
                          value={safePaymentMeta[`monthlyExpense${item}`]}
                          defaultValue={
                            safePaymentMeta[`monthlyExpense${item}`]
                          }
                          InputComponent={FloatingInput}
                          placeholder=""
                          type="text"
                          cleanNumber={cleanNumber}
                          onFormatted={(name, formatted) =>
                            setFormData((p) => ({
                              ...p,
                              paymentMeta: {
                                ...(p?.paymentMeta ?? {}),
                                [`monthlyExpense${item}`]: formatted,
                              },
                            }))
                          }
                        />
                        <label>Monthly Expense</label>
                      </div>

                      <div className="calc-frm-fld form-group sign-of-amount">
                        <FloatingNumber
                          type="text"
                          name={`paymentMeta.monthlyExpense${item}Amount`}
                          control={control}
                          placeholder="0.00"
                          className="inpt-pla"
                          value={safePaymentMeta[`monthlyExpense${item}Amount`]}
                          defaultValue={
                            safePaymentMeta[`monthlyExpense${item}Amount`]
                          }
                          onBlur={(e) =>
                            setFormData((p) => ({
                              ...p,
                              paymentMeta: {
                                ...(p?.paymentMeta ?? {}),
                                [`monthlyExpense${item}Amount`]: e.target.value,
                              },
                            }))
                          }
                        />
                        <label>Amount</label>
                      </div>
                    </div>
                  ))}

                  {/* ✅ Total Monthly Payment */}
                  <div className="calc-frm-field">
                    <div className="calc-frm-fld form-group sign-of-amount">
                      <ControlledField
                        name="totalClosingMeta.totalMonthlyPayment"
                        control={control}
                        value={safeTotalClosingMeta.totalMonthlyPayment}
                        defaultValue={safeTotalClosingMeta.totalMonthlyPayment}
                        InputComponent={FloatingNumber}
                        placeholder=""
                        label="Total Monthly Payment"
                        type="text"
                        cleanNumber={cleanNumber}
                        handleCurrencyBlur={handleCurrencyBlur}
                        onFormatted={(name, formatted) =>
                          setFormData((p) => {
                            // Reverse calculator: the user overrode the calculated
                            // monthly payment, so back-solve the sales price that
                            // would have produced it (recalcSalesPriceFromPayment,
                            // utils/calculations.js). The salesPrice write below also
                            // re-triggers the normal effect chain in useBuyerNetEngine/
                            // useBuyerNetForm (taxes, insurance, PMI, title/escrow fees
                            // — all network-backed, so they land a moment later); we
                            // additionally apply the loan-amount math synchronously
                            // right here so Amount/Down Payment update immediately
                            // instead of waiting on that round trip.
                            const newSalesPrice = recalcSalesPriceFromPayment(
                              p?.totalClosingMeta?.totalMonthlyPaymentOld,
                              formatted,
                              p?.salesPrice,
                            );

                            const updated = {
                              ...p,
                              salesPrice: newSalesPrice,
                              totalClosingMeta: {
                                ...(p?.totalClosingMeta ?? {}),
                                totalMonthlyPayment: formatted,
                              },
                            };

                            if (updated.loanInfoMeta?.buyersLoanType === "Conventional") {
                              const amountResult = getAmountConventional(updated);
                              updated.loanInfoMeta = {
                                ...updated.loanInfoMeta,
                                amount: amountResult.amount,
                                amount2: amountResult.amount2,
                                buyerDownPayment: amountResult.downPayment,
                                conventionalPercent: amountResult.ltv1,
                              };
                            }

                            return updated;
                          })
                        }
                      />
                    </div>
                  </div>

                  {/* ✅ Estimated Tax Prorations */}
                  <div className="calc-frm-field b-top mg-top-16 pd-top-16">
                    <div className="calc-frm-fld form-group sign-of-amount">
                      <ControlledField
                        name="paymentMeta.estimatedTaxProrations"
                        control={control}
                        value={safePaymentMeta.estimatedTaxProrations}
                        defaultValue={safePaymentMeta.estimatedTaxProrations}
                        InputComponent={FloatingNumber}
                        placeholder=""
                        label="Estimated Tax Prorations"
                        type="text"
                        cleanNumber={cleanSignedNumber}
                        inputProps={{ allowNegative: true }}
                        // No handleCurrencyBlur here: useCurrencyFormat's sanitizeNumberInput
                        // (utils/format.js) strips the sign, which would undo allowNegative on
                        // blur. Falls back to ControlledField's own formatCurrency, which is
                        // negative-safe and uses the same 2-decimal precision.
                        onFormatted={(name, formatted) =>
                          setFormData((p) => ({
                            ...p,
                            paymentMeta: {
                              ...(p?.paymentMeta ?? {}),
                              estimatedTaxProrations: formatted,
                            },
                          }))
                        }
                      />
                    </div>
                  </div>

                  {/* ✅ Total At Close */}
                  <div className="totl-estimate box-ly">
                    <div className="totl-estimate-txt heading-c">
                      Total at Close $
                    </div>
                    <div
                      className={`totl-estimate-amnt heading-c ${(safeTotalClosingMeta.totalInvestment ?? 0) < 0
                        ? "text-red"
                        : ""
                        }`}
                    >
                      {formatCurrency(safeTotalClosingMeta.totalInvestment)}
                    </div>
                  </div>

                  {/* ✅ Conditional FHA/USDA minimum cash */}
                  {(loanType === "FHA 203b" || loanType === "USDA") && (
                    <>
                      <div className="totl-estimate box-ly">
                        <div className="totl-estimate-txt heading-g">
                          Buyer must have minimum cash investment $
                        </div>
                        <div className="totl-estimate-amnt heading-g red-txt">
                          {formatCurrency(safeLoanMeta.buyerDownPayment)}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* ✅ Footer Buttons */}
                <div className="box-action">
                  <ul>
                    <li>
                      <button
                        className="icon-pdf"
                        onClick={() =>
                          handleActionButton("download", buyerformData)
                        }
                      >
                        Save PDF
                      </button>
                    </li>
                    <li>
                      <button className="icon-print">Print</button>
                      <ul>
                        <li>
                          <button
                            onClick={() =>
                              handleActionButton("print", buyerformData)
                            }
                          >
                            Estimate
                          </button>
                        </li>
                        {loanType === "Owner Carry" && (
                          <li>
                            <button
                              onClick={() =>
                                handleActionButton(
                                  "AmortizationSch",
                                  buyerformData,
                                )
                              }
                            >
                              Amortization Sch.
                            </button>
                          </li>
                        )}
                      </ul>
                    </li>
                    <li>
                      <button
                        className="icon-email"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          e.preventDefault();
                          const updated = buildBuyerPayload({
                            buyerformData,
                            user,
                          });
                          openModal({
                            title: "Email a Closing Cost Estimate",
                            size: "mediumsizewidth",
                            content: (
                              <EmailModal
                                onClose={closeModal}
                                type="buyer"
                                showToaster={showToaster}
                                buyerFormData={updated}
                                isQuickMode={isQuickMode}
                              />
                            ),
                          });
                        }}
                      >
                        Email
                      </button>
                      <ul>
                        <li>
                          <button
                            onClick={() =>
                              handleActionButton("email", buyerformData)
                            }
                          >
                            Estimate
                          </button>
                        </li>
                        {loanType === "Owner Carry" && (
                          <li>
                            <button
                              onClick={() =>
                                handleActionButton(
                                  "AmortizationSch",
                                  buyerformData,
                                )
                              }
                            >
                              Amortization Sch.
                            </button>
                          </li>
                        )}
                      </ul>
                    </li>
                    <li>
                      <button
                        className="icon-text"
                        onClick={(e) => {
                          const isValid = checkValidSalesPrice();
                          if (!isValid) return;
                          e.preventDefault();
                          const updated = buildBuyerPayload({
                            buyerformData: formData,
                            user,
                          });
                          openModal({
                            title: "Text Message to Customer",
                            size: "mediumsizewidth",
                            content: (
                              <TextMessageModal
                                onClose={closeModal}
                                type="buyer"
                                showToaster={showToaster}
                                buyerFormData={updated}
                                isQuickMode={isQuickMode}
                              />
                            ),
                          });
                        }}
                      >
                        Text
                      </button>
                    </li>
                  </ul>
                </div>
              </div>

              {/* ✅ Disclaimer */}
              <div className="calc-frm-field">
                <a
                  href="#"
                  className="icon-info"
                  onClick={(e) => {
                    e.preventDefault();
                    openModal({
                      title: "Disclaimer",
                      size: "mediumsizewidth",
                      content: (
                        <DisclaimerModal calc="buyer" onClose={closeModal} />
                      ),
                    });
                  }}
                >
                  Disclaimer
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
